import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Video, Maximize2, X, Download, Lock } from 'lucide-react';
import api from '../utils/api';

// Live still preview via the scoped/privacy-gated backend proxy
// (GET /cctv/devices/:id/snapshot). Lightweight by default: loads ONE frame
// (no polling) for list thumbnails so a big camera list doesn't hammer the
// office uplink. Pass refreshMs>0 for a gentle "live" refresh (e.g. a detail
// panel showing one camera at a time).
//
// Pass expandable + name to make the preview clickable → opens a fullscreen
// live lightbox (with download). The lightbox refreshes on its own (~1.5s)
// regardless of the thumbnail's refreshMs.
//
// The /snapshot endpoint enforces company-scope + CCTV access + private_owner_id,
// so a preview only ever appears for a camera the viewer is actually allowed to
// see — safe to drop into any module (CCTV, Assets, etc.).
export default function CameraSnapshot({ deviceId, height = 120, refreshMs = 0, rounded = 8, onResult, expandable = false, name = '', delayMs = 0, apiBase = '/cctv/devices' }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  // 403 = the backend refuses to serve this frame at all: either the per-company
  // video kill switch (cctv_video_blocked) or no access to this camera. Either
  // way it will NEVER succeed, so we latch it and stop polling instead of
  // retrying forever behind a lock icon.
  const [blocked, setBlocked] = useState(false);
  const [fs, setFs] = useState(false);
  const objRef = useRef(null);
  const blockedRef = useRef(false);

  const load = useCallback(async () => {
    if (blockedRef.current) return;
    try {
      const resp = await api.get(`${apiBase}/${deviceId}/snapshot`, { responseType: 'blob' });
      const u = URL.createObjectURL(resp.data);
      if (objRef.current) URL.revokeObjectURL(objRef.current);
      objRef.current = u;
      setUrl(u); setErr(false);
      onResult?.(true);   // camera reachable AND returned a frame (has visual)
    } catch (e) {
      if (e?.response?.status === 403) { blockedRef.current = true; setBlocked(true); }
      setErr(true); onResult?.(false);
    }
  }, [deviceId, onResult, apiBase]);

  useEffect(() => {
    // delayMs staggers the FIRST fetch (and, since the interval starts after it, the
    // refreshes too) so a grid of many channels doesn't hammer one DVR at once — Dahua
    // recorders lock auth on >4-5 rapid snapshot hits.
    let timer;
    const start = setTimeout(() => {
      load();
      if (refreshMs > 0) timer = setInterval(load, refreshMs);
    }, delayMs);
    return () => { clearTimeout(start); if (timer) clearInterval(timer); };
  }, [load, refreshMs, delayMs]);
  // Latched block → tear the polling interval down for good.
  useEffect(() => { if (blocked) blockedRef.current = true; }, [blocked]);
  useEffect(() => () => { if (objRef.current) URL.revokeObjectURL(objRef.current); }, []);

  const canExpand = expandable && url && !err;
  const openFs = (e) => { if (!canExpand) return; e.stopPropagation(); setFs(true); };

  return (
    <>
      <div
        onClick={openFs}
        style={{
          position: 'relative', width: '100%', height, background: '#000',
          borderRadius: rounded, overflow: 'hidden', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: canExpand ? 'zoom-in' : 'default',
        }}
      >
        {url && !err ? (
          <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : blocked ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: 6, textAlign: 'center' }}>
            <Lock size={Math.max(12, Math.min(22, Math.round((typeof height === 'number' ? height : 200) / 4)))} style={{ color: 'var(--c-text-muted)', opacity: 0.55 }} />
            {(typeof height !== 'number' || height >= 90) && (
              <span style={{ fontSize: 9, color: 'var(--c-text-muted)', lineHeight: 1.2 }}>{t('cctv.videoBlocked')}</span>
            )}
          </div>
        ) : err ? (
          <Video size={Math.max(14, Math.min(28, Math.round((typeof height === 'number' ? height : 200) / 3)))} style={{ color: 'var(--c-text-muted)', opacity: 0.35 }} />
        ) : (
          <span style={{ fontSize: 10, color: 'var(--c-text-muted)' }}>…</span>
        )}
        {url && !err && refreshMs > 0 && (
          <span style={{
            position: 'absolute', top: 6, left: 6, display: 'flex', alignItems: 'center', gap: 4,
            background: 'rgba(0,0,0,0.55)', borderRadius: 999, padding: '2px 7px', pointerEvents: 'none',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', animation: 'pulse 1.4s ease-in-out infinite' }} />
            <span style={{ fontSize: 9, fontWeight: 700, color: '#fff', letterSpacing: '0.5px' }}>LIVE</span>
          </span>
        )}
        {canExpand && (
          <span style={{
            position: 'absolute', bottom: 6, right: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.55)', borderRadius: 6, padding: 4, pointerEvents: 'none',
          }}>
            <Maximize2 size={Math.max(11, Math.min(16, Math.round(height / 9)))} style={{ color: '#fff' }} />
          </span>
        )}
      </div>
      {fs && <CameraLightbox deviceId={deviceId} name={name} apiBase={apiBase} onClose={() => setFs(false)} />}
    </>
  );
}

// Fullscreen live view — own fetch loop (~1.5s), centered to the viewport,
// portal'd to document.body so no ancestor transform can mis-center it.
export function CameraLightbox({ deviceId, name, onClose, apiBase = '/cctv/devices' }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const objRef = useRef(null);
  // Mismo latch que CameraSnapshot: un 403 VIDEO_BLOCKED nunca se va a resolver
  // reintentando. Sin esto el lightbox martillaba 1 req/s para siempre — y no es
  // teórico: CctvCameraWidget lo usa suelto, sin el latch del componente padre.
  const blockedRef = useRef(false);

  const load = useCallback(async () => {
    if (blockedRef.current) return;
    try {
      const resp = await api.get(`${apiBase}/${deviceId}/snapshot`, { responseType: 'blob' });
      const u = URL.createObjectURL(resp.data);
      if (objRef.current) URL.revokeObjectURL(objRef.current);
      objRef.current = u;
      setUrl(u); setErr(false);
    } catch (e) {
      if (e?.response?.status === 403) { blockedRef.current = true; setBlocked(true); }
      setErr(true);
    }
  }, [deviceId, apiBase]);

  useEffect(() => { blockedRef.current = false; setBlocked(false); }, [deviceId]);

  useEffect(() => {
    if (blocked) return undefined;   // ya latcheado: ni fetch inicial ni intervalo
    load();
    // Backend serves frames stale-while-revalidate, so each poll returns instantly
    // and the image refreshes as fast as the camera produces frames (~1s feel).
    const timer = setInterval(load, 1000);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { clearInterval(timer); window.removeEventListener('keydown', onKey); };
  }, [load, onClose]);
  useEffect(() => () => { if (objRef.current) URL.revokeObjectURL(objRef.current); }, []);

  const download = (e) => {
    e.stopPropagation();
    if (!objRef.current) return;
    const a = document.createElement('a');
    a.href = objRef.current;
    const safe = (name || 'camera').replace(/[^\w.-]+/g, '_');
    a.download = `${safe}_${Date.now()}.jpg`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const modal = (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) { (onClose)(e); } }}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.92)', padding: 24,
      }}
    >
      {/* top bar */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, height: 56, padding: '0 16px',
          display: 'flex', alignItems: 'center', gap: 10, color: '#fff',
          background: 'linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0))',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', animation: 'pulse 1.4s ease-in-out infinite' }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.5px' }}>LIVE</span>
        </span>
        {name && <span style={{ fontWeight: 600, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button type="button" onClick={download} className="btn btn-ghost btn-sm" style={{ color: '#fff' }} disabled={!url || err}>
            <Download size={16} /> {t('cctv.fullscreen.download')}
          </button>
          <button type="button" onClick={onClose} className="btn btn-ghost btn-icon" style={{ color: '#fff' }} aria-label={t('common.close')}>
            <X size={20} />
          </button>
        </div>
      </div>

      {url && !err ? (
        <img
          onClick={(e) => e.stopPropagation()}
          src={url}
          alt={name || ''}
          style={{ maxWidth: '95vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 6, boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}
        />
      ) : err ? (
        <div style={{ color: 'var(--c-text-muted)', textAlign: 'center' }}>
          <Video size={48} style={{ opacity: 0.4 }} />
          <div style={{ marginTop: 8 }}>{t('cctv.fullscreen.unreachable')}</div>
        </div>
      ) : (
        <span style={{ color: '#fff', fontSize: 14 }}>…</span>
      )}
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
